<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'

import { Button } from '@/components/ui/button'
import { ProjectBreadcrumbsItem } from '@/components/breadcrumbs'
import { ChevronRight, House, Building2, Folders, Layers } from 'lucide-vue-next'

const store = useStore()
const router = useRouter()
const route = useRoute()

// Route params
const orgId = computed(() => {
  const id = route.params.org_id
  return id ? parseInt(id) : null
})
const projectId = computed(() => {
  const id = route.params.project_id
  return id ? parseInt(id) : null
})
const featureCode = computed(() => route.params.featureCode)
const layerId = computed(() => route.params.layer_id)

// Store getters
const projects = computed(() => store.getters['project/getProjects'])
const flatedProjects = computed(() => store.getters['project/getFlatedProjects'])
const feature = computed(() => store.getters['geo/getCurrentFeature'])
const currentOrg = computed(() => store.getters['org/getCurrent'])
const orgLayers = computed(() => {
  return orgId.value ? store.getters['layers/getOrgLayers'](orgId.value) : []
})

// Get organization info - with fallback
const orgInfo = computed(() => {
  if (currentOrg.value) return currentOrg.value

  // Fallback: try to get org from the organizations list
  const orgs = store.getters['org/getOrgs']
  if (orgs && orgs.length > 0 && orgId.value) {
    return orgs.find(org => org.organization_id === orgId.value)
  }

  return null
})

// Get current layer if viewing layer detail
const currentLayer = computed(() => {
  if (!layerId.value || !orgLayers.value) return null
  return orgLayers.value.find(layer => layer.uuid === layerId.value)
})

// Determine current route type
const routeType = computed(() => {
  if (route.name === 'OrgProjects') return 'projects'
  if (route.name === 'OrgLayers') return 'layers'
  if (route.name === 'LayerCreate') return 'layer-create'
  if (route.name === 'LayerDetail') return 'layer-detail'
  if (projectId.value) return 'project'
  return 'other'
})

// Project hierarchy (same as before but with organization context)
const selectedProjectTree = computed(() => {
  if (!projectId.value || projects.value.length === 0) {
    return []
  }

  const selectedProject = flatedProjects.value.find(i => i.project_id === projectId.value)
  if (!selectedProject) {
    return []
  }

  const tree = []
  let current = selectedProject
  while (current) {
    tree.unshift(current)
    current = flatedProjects.value.find(i => i.project_id === current.parent_id)
  }

  return tree
})

// Check if we should show breadcrumbs
const shouldShowBreadcrumbs = computed(() => {
  // Temporarily always show breadcrumbs for debugging
  return true
})

const goHome = () => {
  router.push({ name: 'Dashboard' })
}

const goToOrg = () => {
  if (!orgId.value) return
  router.push({ name: 'OrgProjects', params: { org_id: orgId.value } })
}

const goToProjects = () => {
  if (!orgId.value) return
  router.push({ name: 'OrgProjects', params: { org_id: orgId.value } })
}

const goToLayers = () => {
  if (!orgId.value) return
  router.push({ name: 'OrgLayers', params: { org_id: orgId.value } })
}
</script>

<template>
  <div v-if="shouldShowBreadcrumbs" class="bg-white rounded-full pr-2 inline-flex items-center flex-wrap shadow-md">
    <!-- Debug info (remove after fixing) -->
    <!-- <div class="text-xs text-gray-500 px-2">
      Debug: orgId={{ orgId }}, route={{ route.name }}, type={{ routeType }}
    </div> -->

    <!-- Home button - only show if there are other breadcrumb items -->
    <Button
      v-if="orgInfo || orgId"
      variant="ghost"
      size="sm"
      class="rounded-full w-7 h-7 p-1 text-gray-400 hover:text-primary"
      @click="goHome"
    >
      <House class="w-4 h-4" />
    </Button>

    <!-- Organization -->
    <template v-if="orgInfo">
      <ChevronRight class="w-4 h-4 text-gray-400" />
      <Button variant="ghost" size="sm" class="rounded-full" @click="goToOrg">
        <Building2 class="w-4 h-4 mr-1" />
        <span class="max-w-[150px] truncate">{{ orgInfo.organization_name }}</span>
      </Button>
    </template>

    <!-- Show fallback if no orgInfo -->
    <template v-if="!orgInfo && orgId">
      <ChevronRight class="w-4 h-4 text-gray-400" />
      <Button variant="ghost" size="sm" class="rounded-full" disabled>
        <Building2 class="w-4 h-4 mr-1" />
        <span>Organization {{ orgId }}</span>
      </Button>
    </template>

    <!-- Projects section -->
    <template v-if="routeType === 'projects' || projectId">
      <ChevronRight class="w-4 h-4 text-gray-400" />
      <Button
        variant="ghost"
        size="sm"
        class="rounded-full"
        :disabled="routeType === 'projects'"
        @click="goToProjects"
      >
        <Folders class="w-4 h-4 mr-1" />
        <span>Projects</span>
      </Button>

      <!-- Project hierarchy -->
      <ProjectBreadcrumbsItem
        v-for="project in selectedProjectTree"
        :key="project.project_id"
        :project="project"
        :current-project-id="projectId"
      />

      <!-- Feature -->
      <div v-if="featureCode && feature" class="flex items-center">
        <ChevronRight class="w-4 h-4 text-gray-400" />
        <Button variant="ghost" size="sm" disabled>
          <span class="max-w-[200px] truncate">{{ feature.properties.name }}</span>
        </Button>
      </div>
    </template>

    <!-- Layers section -->
    <template v-if="routeType === 'layers' || routeType === 'layer-create' || routeType === 'layer-detail'">
      <ChevronRight class="w-4 h-4 text-gray-400" />
      <Button
        variant="ghost"
        size="sm"
        class="rounded-full"
        :disabled="routeType === 'layers'"
        @click="goToLayers"
      >
        <Layers class="w-4 h-4 mr-1" />
        <span>Layers</span>
      </Button>

      <!-- Layer Create -->
      <template v-if="routeType === 'layer-create'">
        <ChevronRight class="w-4 h-4 text-gray-400" />
        <Button variant="ghost" size="sm" disabled>
          <span>New Layer</span>
        </Button>
      </template>

      <!-- Layer Detail -->
      <template v-if="routeType === 'layer-detail' && currentLayer">
        <ChevronRight class="w-4 h-4 text-gray-400" />
        <Button variant="ghost" size="sm" disabled>
          <span class="max-w-[200px] truncate">{{ currentLayer.name || currentLayer.title || 'Layer Details' }}</span>
        </Button>
      </template>
    </template>
  </div>
</template>
